import React, { createContext, useContext, RefObject } from 'react';
import { ChatMessage, Reference, ToolCallEntry } from '../interfaces';
import { CurrentChatInfo } from '../types/CurrentChatInfo';

// Types
type LogoVariant = 'light' | 'dark';
type LogoLoadState = Record<LogoVariant, boolean>;

type TextPreview = {
  content: string;
  truncated: boolean;
};

interface ChatItem {
  chat_id: string;
  title: string;
  updated_at?: string;
}

export interface PlaygroundContextValue {
  // === Theme & Layout ===
  dark: boolean;
  toggleDark: () => void;
  isMobile: boolean;
  sidebarWidth: number;
  sidebarLogosLoaded: LogoLoadState;
  inputBottomOffset: number;

  // === Sidebar State ===
  openSidebar: boolean;
  effectiveSidebarCollapsed: boolean;
  handleSidebarToggle: (open: boolean) => void;
  toggleSidebarCollapse: () => void;

  // === Chat State (from ChatControlContext) ===
  activeChatID: CurrentChatInfo;
  setActiveChatID: (p: CurrentChatInfo) => void;
  messageHistory: ChatMessage[];
  setMessageHistory: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  userChats: object[];
  setUserChats: (p: object[]) => void;
  modelChat: string;
  setModelChat: (model: string) => void;
  loadingHistory: boolean;
  loadingInitialChats: boolean;
  loadingChatHistory: boolean;

  // === Chat List Management ===
  editingChatId: string | null;
  setEditingChatId: (chatId: string | null) => void;
  editedChatTitle: string;
  setEditedChatTitle: (title: string) => void;
  hoveredChatId: string | null;
  setHoveredChatId: (chatId: string | null) => void;
  chatMenuClickedChatId: string | null;
  anchorElChatMenu: HTMLElement | null;
  openChatMenu: boolean;
  handleOpenChatMenu: (event: React.MouseEvent<HTMLElement>, chatId: string, title: string) => void;
  handleCloseChatMenu: () => void;
  handleMenuItemClick: (event: React.MouseEvent<HTMLElement>, action: 'rename' | 'delete') => void;
  handleChatItemClick: (chatId: string) => void;
  handleRenameConversation: (chatId: string, newTitle: string) => void;
  handleNewChat: () => void;
  fetchMoreChats: () => void;
  formatChatTitle: (title: string) => string;

  // === Chat Search ===
  searchQuery: string;
  searchResults: object[] | null;
  isSearching: boolean;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearSearch: () => void;

  // === Model Selection ===
  openDropdownMenu: boolean;
  setOpenDropdownMenu: (open: boolean) => void;
  dropdownMenuOptions: string[];
  modelDescriptions: Record<string, [string, string[]]>;
  handleSelectOption: (option: string) => void;

  // === User Menu ===
  openUserMenu: boolean;
  setOpenUserMenu: (open: boolean) => void;
  userPicture: string;
  userEmail?: string;
  displayedName: string;
  handleLogoutButton: () => void;
  handlePlatformButton: () => void;

  // === Modals ===
  setOpenHelpModal: (open: boolean) => void;
  setOpenAccountModal: (open: boolean) => void;
  setOpenSubscriptionModal: (open: boolean) => void;
  setOpenERPModal: (open: boolean) => void;
  handleClearConversationButton: () => void;
  promptBankOpen: boolean;
  setPromptBankOpen: (open: boolean) => void;

  // === Message Generation ===
  messageBeingGenerated: string;
  loadingGeneration: boolean;
  loadingMessage: string;
  extractingFiles: string[];
  chatErrorMessage: string;
  allowErrorRetry: boolean;
  showPlanUpgradeOption: boolean;
  backgroundGeneratingChatIds: Set<string>;
  completedBackgroundChatIds: Set<string>;
  cancellingBackground: boolean;

  // === Debug Mode ===
  debugMode: boolean;
  currentToolCalls: ToolCallEntry[];

  // === Message Editing ===
  isEditing: boolean;
  editingMessageId: number | null;
  messageBeingEdited: string;
  handleEditMessage: (index: number, content: string) => void;
  handleSaveEditedMessage: (e: React.FormEvent) => void;
  handleCancelEdit: () => void;
  handleEditMessageInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

  // === Message Actions ===
  copiedIndex: number | null;
  handleCopyToClipboard: (content: string, index: number, references?: Reference[]) => void;
  handleLikeMessage: (index: number) => void;
  handleDislikeClick: (e: React.MouseEvent<HTMLButtonElement>, index: number) => void;
  handleRegenerateMessage: (index: number) => void;
  handleRetryMessage: () => void;

  // === File Handling ===
  filePreviews: Record<string, string>;
  fileTextPreviews: Record<string, TextPreview>;
  numPages: Record<string, number> | null;
  fileDownloadProgress: Record<string, number>;
  selectedFiles: File[] | null;
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[] | null>>;
  fileInputKey: number;
  fileInputRef: RefObject<HTMLInputElement | null>;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOpenPreview: (filename: string) => void;
  renderSelectedFilesPreview: (context: 'landing' | 'input') => React.ReactNode;
  modelsWithFileUploadAllowed: string[];

  // === OCR ===
  loadingOcrModal: boolean;
  handleOpenOcrModal: (message: any) => void;
  hasOcrContentForMessage: (message: any) => boolean;

  // === Quota & Plan ===
  planStatus: string | null;
  freeQuota: { messages: number } | null;
  quotaBadgeIndex: number | null;
  renderQuotaInfoTrigger: (variant: 'default' | 'remaining') => React.ReactNode;

  // === Message Formatting ===
  formatAssistantMessage: (content: string, references?: Reference[], isStreaming?: boolean) => React.ReactNode;
  getVisibleReferences: (references: Reference[] | undefined, index: number, content?: string) => Reference[];
  getCitedReferencesWithHistory: (messageReferences: Reference[] | undefined, content?: string) => { references: Reference[]; hasHistoryRefs: boolean };

  // === Input Area ===
  currentMessage: string;
  setCurrentMessage: (message: string) => void;
  handleNewMessageInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleButtonDownTextInput: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  isAtBottom: boolean;
  smoothScrollToEnd: () => void;

  // === Audio Recording ===
  isRecording: boolean;
  status: string;
  sendingAudio: boolean;
  audioTooltipOpen: boolean;
  setAudioTooltipOpen: (value: boolean) => void;
  handleAudioButtonClick: () => void;
  handleStopClick: () => void;
  partialTranscript: string;
  sttTranscribing: boolean;

  // === Web Search ===
  webSearch: boolean;
  setWebSearch: (value: boolean) => void;
  modelsWithWebSearchAllowed: string[];

  // === Data Ocean ===
  dataOcean: boolean;
  setDataOcean: (value: boolean) => void;
  modelsWithDataOceanToggle: string[];

  // === Usar competidor (build-flag gated) ===
  useCompetitor: boolean;
  setUseCompetitor: (value: boolean) => void;

  // === Reasoning ===
  reasoning: boolean;
  setReasoning: (value: boolean) => void;

  // === Precise Location (opt-in) ===
  usePreciseLocation: boolean;
  togglePreciseLocation: () => void;
  locatingUser: boolean;

  // === Code Execution ===
  codeExecution: boolean;
  setCodeExecution: (value: boolean) => void;
  modelsWithCodeExecutionAllowed: string[];

  // === Deep Research ===
  deepResearch: boolean;
  setDeepResearch: (value: boolean) => void;
  deepResearchMode: "full" | "light";
  setDeepResearchMode: (value: "full" | "light") => void;
  deepResearchPhase: string;
  deepResearchProgress: number;
  deepResearchPlan: any[];
  deepResearchSteps: Record<number, any>;
  deepResearchTokens: { input: number; output: number };
  panoramaPhase: string;
  panoramaProgress: number;
  panoramaNSearches: number;
  panoramaNDocs: number;
  panoramaNTheses: number;
  panoramaInputTokens: number;
  panoramaOutputTokens: number;
  panoramaDetail: string;

  // === Refs ===
  endOfChatMessageMarkerRef: RefObject<HTMLDivElement | null>;
}

const PlaygroundContext = createContext<PlaygroundContextValue | null>(null);

export const usePlayground = (): PlaygroundContextValue => {
  const context = useContext(PlaygroundContext);
  if (!context) {
    throw new Error('usePlayground must be used within a PlaygroundProvider');
  }
  return context;
};

export const PlaygroundProvider = PlaygroundContext.Provider;

export default PlaygroundContext;
